/**
 * Transaction Range Service
 * Fetches transaction range breakdown data (deal size buckets)
 */

// Color palette removed - using hand-picked professional colors instead

export interface RangePeriodData {
  funding: number;
  deals: number;
  avgDeal: number;
}

export interface TransactionRangeData {
  [range: string]: {
    [period: string]: RangePeriodData;
  };
}

export interface TransactionRangeResponse {
  success: boolean;
  data: TransactionRangeData;
  ranges: string[];
  periods: string[];
  groupBy: 'year' | 'quarter';
  metadata: {
    timeframe: string;
    filters_applied: boolean;
    current_period: {
      start: string;
      end: string;
    };
  };
  cache_status?: string;
  response_time_ms?: number;
}

class TransactionRangeService {
  private baseUrl: string;

  constructor(baseUrl?: string) {
    this.baseUrl = baseUrl || import.meta.env.VITE_API_ENDPOINT || 'https://spog-aggregation.mike-532.workers.dev';
  }

  /**
   * Fetch transaction range breakdown data
   */
  async getTransactionRangeBreakdown(
    groupBy: 'year' | 'quarter' = 'year',
    timeframe: string = '12w',
    weekOffset: number = 0,
    filters?: Record<string, string[] | undefined>
  ): Promise<TransactionRangeResponse> {
    const params = new URLSearchParams({
      groupBy,
      timeframe,
      weekOffset: weekOffset.toString()
    });

    // Add filters to query params
    if (filters) {
      Object.entries(filters).forEach(([key, values]) => {
        if (values && values.length > 0) {
          params.set(key, values.join(','));
        }
      });
    }

    const url = `${this.baseUrl}/api/v2/ranges/breakdown?${params.toString()}`;

    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data: TransactionRangeResponse = await response.json();

      if (!data.success) {
        throw new Error('API returned unsuccessful response');
      }

      return data;
    } catch (error) {
      console.error('Failed to fetch transaction range breakdown:', error);
      throw error;
    }
  }

  /**
   * Get range display name and order
   * Updated to match granular ranges in Airtable data
   */
  getRangeOrder(): string[] {
    return [
      '$0-$100k',
      '$100k-$1M',
      '$1M-$5M',
      '$5M-$10M',
      '$10M-$25M',
      '$25M-$50M',
      '$50M-$100M',
      '$100M-$250M',
      '$250M-$500M',
      '$500M-$1B',
      '≥ $1B',
      'Undisclosed'
    ];
  }

  /**
   * Get range color for visualization
   * Uses earth tones with strategic greens and reds for standout
   * Professional palette with reds highlighting larger deals
   */
  getRangeColor(range: string): string {
    // Earth tone palette - greens for small/medium, reds for large deals
    const RANGE_COLORS: Record<string, string> = {
      '$0-$100k': '#A8DADC',       // Pale teal
      '$100k-$1M': '#B4C7A4',      // Pale olive
      '$1M-$5M': '#7BA05B',        // Olive green
      '$5M-$10M': '#2D6A4F',       // Forest green
      '$10M-$25M': '#D4A574',      // Tan
      '$25M-$50M': '#C77C3A',      // Burnt sienna
      '$50M-$100M': '#B85C38',     // Terracotta
      '$100M-$250M': '#A64B2A',    // Rust orange
      '$250M-$500M': '#C9485B',    // Burgundy red - large deal standout
      '$500M-$1B': '#9B2335',      // Wine red
      '≥ $1B': '#7A1E2A',          // Deep red - largest deals standout
      'Undisclosed': '#6B7280'     // Gray
    };

    return RANGE_COLORS[range] || '#9CA3AF'; // Gray-400 fallback
  }
}

export const transactionRangeService = new TransactionRangeService();
